import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppDispatch } from '../../../store';
import Button from '../../shared/TailwindComponents/Button';
import Table from '../../shared/TailwindComponents/Table';
import Badge from '../../shared/TailwindComponents/Badge';
import CreateDataSourceForm from './CreateDataSourceForm';
import DeleteDataSourceConfirmation from './DeleteDataSourceConfirmation';
import Header, { Action } from '../../shared/TailwindComponents/Header';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import ListItem from '../../shared/TailwindComponents/ListItem';
import { PiBuildingLight, PiUserLight, PiDatabaseLight, PiTrashLight, PiSpinnerGapLight, PiHourglassLight, PiClockLight } from 'react-icons/pi';
import { BadgeItem } from '../../shared/TailwindComponents/types/badgeTypes';
import { deleteDataSource, deleteDataSourcesBulk, fetchDataSources } from '../../../store/actions/AIStudioAction';
import { showModal, hideModal } from '../../../store/actions/modalActions';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import MiniLoadingToCheckmark from '../../shared/animations/MiniLoadingToCheckmark';
import SkeletonClientItem from '../../Clients/SkeletonClientItem';
import CollaboratorBadge from '../../shared/TailwindComponents/CollaboratorsBadge';
import { formatDateTime } from '../../../utils/functions';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import { useProcessingPubSub } from '../../../hooks/useProcessingPubSub';
import ResourceTrash from '../../RecycleBin/ResourceTrash';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, dataSourcesListTourSteps } from '../../shared/FeatureTour';

const PAGE_SIZE = 50;

const processingStatusToColorMap: Record<string, string> = {
  active: 'green',
  indexing: 'blue',
  pending_indexing: 'blue',
  queued: 'orange',
  failed: 'red',
  pending: 'orange',
};

const DataSourcesListPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [allDataSources, setAllDataSources] = useState<any[]>([]);
  const [isCreatingDataSource, setIsCreatingDataSource] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [isLoading, setIsLoading] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [trashBulkActions, setTrashBulkActions] = useState<{ label: string; onClick: () => void; icon?: React.ReactElement }[]>([]);
  const [trashSelectedCount, setTrashSelectedCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalDataSources, setTotalDataSources] = useState(0);
  const [selectedDataSources, setSelectedDataSources] = useState<string[]>([]);
  const location = useLocation();

  const totalActiveFilters = Object.values(selectedFilters).reduce((acc, vals) => acc + vals.length, 0);

  const { checkSectionGate } = useLicenseGate();

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'data-sources-list',
    steps: dataSourcesListTourSteps,
    readyToShow: !isLoading && !isCreatingDataSource && allDataSources.length > 0,
    autoStartDelay: 600,
  });

  // ── PubSub: live processing tracking ──
  const fetchRef = useRef<() => void>(() => {});
  const activeProcessingStatuses = new Set(['indexing', 'pending', 'queued', 'pending_indexing']);

  const { processingStates, joinProcessingGroups, reattachListener } = useProcessingPubSub({
    entityType: 'datasource',
    getGroupName: (item: any) => `datasource_${item.uuid}`,
    getItemUuid: (item: any) => item.uuid,
    isProcessing: (item: any) => activeProcessingStatuses.has(item.status),
    onRefetch: () => fetchRef.current(),
    enabled: !isCreatingDataSource,
  });

  // Join PubSub groups when datasource items update (works for reindex events)
  useEffect(() => {
    if (!isCreatingDataSource && allDataSources.length > 0) {
      joinProcessingGroups(allDataSources);
    }
  }, [allDataSources, isCreatingDataSource, joinProcessingGroups]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('add') === 'true') {
      if (checkSectionGate('data_sources')) {
        setIsCreatingDataSource(true);
      } else {
        history.replace('/data-sources');
      }
    } else {
      setIsCreatingDataSource(false);
    }
  }, [location]);

  // ── Fetch logic ──
  const fetchInitialDataSources = useCallback(async () => {
    setIsLoading(true);
    const resp: any = await dispatch(fetchDataSources({
      searchTerm: searchQuery,
      sortBy,
      sortOrder,
      page: 1,
      limit: PAGE_SIZE,
    }) as any);
    if (resp) {
      setAllDataSources(resp.dataSources || []);
      setTotalDataSources(resp.total || 0);
      setPage(1);
      setHasMore((resp.dataSources?.length || 0) === PAGE_SIZE && (resp.currentPage || 1) < (resp.totalPages || 1));
    }
    setIsLoading(false);
  }, [dispatch, searchQuery, sortBy, sortOrder]);

  const loadMoreDataSources = useCallback(async () => {
    if (!hasMore || isLoading) return;
    setIsLoading(true);
    const nextPage = page + 1;
    const resp: any = await dispatch(fetchDataSources({
      searchTerm: searchQuery,
      sortBy,
      sortOrder,
      page: nextPage,
      limit: PAGE_SIZE,
    }) as any);
    if (resp) {
      setAllDataSources(prev => [...prev, ...(resp.dataSources || [])]);
      setPage(nextPage);
      setHasMore((resp.dataSources?.length || 0) === PAGE_SIZE && (resp.currentPage || nextPage) < (resp.totalPages || 1));
    }
    setIsLoading(false);
  }, [dispatch, hasMore, isLoading, page, searchQuery, sortBy, sortOrder]);

  // Keep fetchRef in sync so PubSub callback always calls latest version
  useEffect(() => {
    fetchRef.current = fetchInitialDataSources;
  }, [fetchInitialDataSources]);

  // Re-attach PubSub listener when coming back from creation form
  const prevIsCreatingRef = useRef(isCreatingDataSource);
  useEffect(() => {
    if (prevIsCreatingRef.current && !isCreatingDataSource) {
      reattachListener();
      fetchInitialDataSources();
    }
    prevIsCreatingRef.current = isCreatingDataSource;
  }, [isCreatingDataSource, fetchInitialDataSources, reattachListener]);

  // Re-fetch on filter/sort change
  useEffect(() => {
    if (isCreatingDataSource) return;
    fetchInitialDataSources();
  }, [searchQuery, sortBy, sortOrder, isCreatingDataSource, fetchInitialDataSources]);

  // Poll for status updates when items are in processing state
  useEffect(() => {
    if (isCreatingDataSource) return;
    const hasProcessingItems = allDataSources.some((ds: any) => activeProcessingStatuses.has(ds.status));
    if (!hasProcessingItems) return;

    const interval = setInterval(() => {
      fetchInitialDataSources();
    }, 8000);

    return () => clearInterval(interval);
  }, [allDataSources, isCreatingDataSource, fetchInitialDataSources]);

  // ── Handlers ──
  const handleAddDataSourceClick = () => {
    setIsCreatingDataSource(true);
    history.push('/data-sources?add=true');
  };

  const handleAddDataSourceClose = () => {
    setIsCreatingDataSource(false);
    history.push('/data-sources');
  };

  const handleSearchChange = useCallback(
    debounce((query: string) => {
      setSearchQuery(query);
      setPage(1);
    }, 300),
    []
  );

  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    setSelectedFilters((prev) => ({ ...prev, [filterName]: selectedValues }));
    setPage(1);
  };

  const handleSortChange = (newSortBy: string, newSortOrder?: 'asc' | 'desc') => {
    if (newSortOrder) {
      const columnToFieldMap: Record<string, string> = {
        'Name': 'name',
        'Created At': 'created_at',
        'Documents': 'document_count',
        'Status': 'status',
      };
      setSortBy(columnToFieldMap[newSortBy] || newSortBy);
      setSortOrder(newSortOrder);
    } else {
      const parts = newSortBy.split('_');
      const order = parts.pop() as 'asc' | 'desc';
      const field = parts.join('_');
      setSortBy(field);
      setSortOrder(order);
    }
    setPage(1);
  };

  const sortOptions = useMemo(() => [
    { label: t('Name (A-Z)'), value: 'name_asc', current: sortBy === 'name' && sortOrder === 'asc' },
    { label: t('Name (Z-A)'), value: 'name_desc', current: sortBy === 'name' && sortOrder === 'desc' },
    { label: t('Newest First'), value: 'created_at_desc', current: sortBy === 'created_at' && sortOrder === 'desc' },
    { label: t('Oldest First'), value: 'created_at_asc', current: sortBy === 'created_at' && sortOrder === 'asc' },
  ], [t, sortBy, sortOrder]);

  // ── Delete Handlers ──
  const handleDeleteDataSources = (dsIds: string[]) => {
    if (dsIds.length === 0) {
      dispatch(showFeedbackModal({
        modalType: 'warning',
        message: t('Please select at least one data source.'),
        showModal: true,
        duration: 3000,
      }));
      return;
    }
    dispatch(showModal({
      type: 'custom',
      message: t(`Are you sure you want to delete ${dsIds.length > 1 ? 'these data sources' : 'this data source'}?`),
      subMsg: t('This action cannot be undone.'),
      icon: <PiTrashLight className='w-6 h-6 text-red-600' />,
      width: '500px',
      content: (
        <DeleteDataSourceConfirmation
          count={dsIds.length}
          onConfirm={async () => {
            if (dsIds.length > 1) {
              await dispatch(deleteDataSourcesBulk(dsIds) as any);
            } else {
              await dispatch(deleteDataSource(dsIds[0]) as any);
            }
            fetchInitialDataSources();
            setSelectedDataSources([]);
            dispatch(hideModal());
          }}
          onCancel={() => dispatch(hideModal())}
        />
      ),
      onCancel: () => { },
      showModal: true,
    }));
  };

  const dataSourceActions = [
    {
      label: t('Delete Data Sources'),
      onClick: (dsIds: string[]) => handleDeleteDataSources(dsIds),
      icon: <PiTrashLight className="h-4 w-4" />,
    },
  ];

  const tableActions = [
    {
      label: t('Delete Data Source'),
      onClick: (row: any) => handleDeleteDataSources([row.id]),
      icon: <PiTrashLight className="h-4 w-4" />,
      isDestructive: true,
    },
  ];

  const filters = [
    {
      id: 'DataSourceType',
      name: t('Data Source Type'),
      options: [
        { value: 'Personal', label: t('Personal') },
        { value: 'Shared', label: t('Shared') },
        { value: 'Company', label: t('Company') },
      ],
    },
  ];

  const actions: Action[] = isCreatingDataSource
    ? [{ label: t('Cancel'), onClick: handleAddDataSourceClose, variant: 'outline' }]
    : [{ label: t('Create Data Source'), onClick: handleAddDataSourceClick, variant: 'primary' }];

  const formatSize = (bytes?: number) => {
    if (!bytes && bytes !== 0) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let i = -1;
    let size = bytes;
    do { size = size / 1024; i++; } while (size >= 1024 && i < units.length - 1);
    return `${size.toFixed(1)} ${units[i]}`;
  };

  // ── Columns & Table Data ──
  const columns = useMemo(() => ['Name', 'Status', 'Documents', 'Size', 'Access', 'Created At', 'Actions'], []);
  const sortableColumns = useMemo(() => ['Name', 'Created At', 'Documents', 'Status'], []);

  const tableData = useMemo(() => {
    return allDataSources.map((ds: any) => {
      const allCollaborators = (ds.collaborators || [])
        .filter((s: any) => s.name || s.email || s.initials)
        .map((s: any) => ({
          name: s.name || s.email || '?',
          initials: s.initials || (s.name ? s.name.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() : '?'),
          avatarURL: s.avatarUrl || null,
          email: s.email,
          access: s.access || s.role || 'collaborator',
        }));

      const statusLabel = ds.status || 'active';
      const statusColor = processingStatusToColorMap[statusLabel] || 'gray';
      const liveState = processingStates[ds.uuid];
      const isProcessing = statusLabel === 'indexing' || statusLabel === 'pending_indexing';
      const isPending = statusLabel === 'pending';
      const isQueued = statusLabel === 'queued';
      const isFailed = statusLabel === 'failed';
      const isCompleted = statusLabel === 'active';
      const isClickable = isCompleted;

      return {
        id: ds.uuid,
        processing_status: statusLabel,
        _isClickable: isClickable,
        'Name': (
          <div className="flex flex-col gap-0.5" style={{ opacity: isFailed ? 0.6 : 1 }}>
            {isProcessing ? (
              <div className="flex items-center gap-2">
                <MiniLoadingToCheckmark isLoading={true} />
                <div className="flex-1 min-w-0">
                  {liveState?.step ? (
                    <>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-blue-600 dark:text-blue-400 font-medium truncate">
                          {(liveState.step || '').replace(/_/g, ' ')}
                        </span>
                      </div>
                      {liveState.message && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                          {liveState.message}
                        </div>
                      )}
                      {liveState.percent != null && liveState.percent > 0 && (
                        <div className="mt-1 h-1 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 to-blue-700 transition-all duration-700 ease-in-out rounded-full"
                            style={{ width: `${liveState.percent}%` }}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="text-sm text-gray-500 dark:text-gray-400 italic">Indexing data source...</div>
                      <div className="h-3 w-2/3 bg-gray-300 dark:bg-gray-600 rounded mt-1 animate-pulseFade" />
                    </>
                  )}
                </div>
              </div>
            ) : isFailed ? (
              <div className="flex items-center gap-2">
                <div className="text-sm text-red-500 dark:text-red-400 italic">Indexing failed</div>
              </div>
            ) : (isPending || isQueued) ? (
              <div className="flex items-center gap-2">
                <PiHourglassLight className="h-4 w-4 text-orange-400 animate-pulse flex-shrink-0" />
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                    {isPending ? 'Waiting to be queued...' : 'Queued for indexing...'}
                  </div>
                </div>
              </div>
            ) : (
              <span>{ds.name}</span>
            )}
          </div>
        ),
        'Status': (
          <div className="flex items-center gap-2">
            <Badge
              className='capitalize'
              color={statusColor as any}
              icon={
                isProcessing ? <PiSpinnerGapLight className="animate-spin" /> :
                (isPending || isQueued) ? <PiClockLight className="text-orange-400" /> :
                undefined
              }
            >
              {isProcessing ? 'Indexing' :
               isQueued ? 'Queued' :
               isPending ? 'Pending' :
               statusLabel}
            </Badge>
          </div>
        ),
        'Documents': String(ds.document_count || 0),
        'Size': formatSize(ds.total_size_bytes),
        'Access': allCollaborators.length > 0 ? (
          <div className="flex items-center">
            {allCollaborators.slice(0, 5).map((collab: any, idx: number) => (
              <div key={idx} className={clsx(idx > 0 && 'ml-[-0.5rem]')} style={{ zIndex: 5 - idx }}>
                <CollaboratorBadge collaborator={collab} />
              </div>
            ))}
            {allCollaborators.length > 5 && (
              <div className="ml-[-0.5rem] h-7 w-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300 border-2 border-white dark:border-gray-800" style={{ zIndex: 0 }}>
                +{allCollaborators.length - 5}
              </div>
            )}
          </div>
        ) : <span className="text-gray-400 text-xs">&mdash;</span>,
        'Created At': formatDateTime(ds.created_at),
      };
    });
  }, [allDataSources, processingStates]);

  const gridData = useMemo(() => {
    return allDataSources.map((ds: any) => {
      const typeLabel = ds.visibility === 'private' ? 'Personal' : ds.visibility === 'organization' ? 'Company' : 'Shared';
      const statusLabel = ds.status || 'active';
      const badges: BadgeItem[] = [
        { label: typeLabel, color: 'blue' },
        { label: statusLabel, color: (processingStatusToColorMap[statusLabel] || 'gray') as any },
      ];
      const details = [
        { label: t('Documents'), value: (ds.document_count || 0).toString() },
        { label: t('Size'), value: formatSize(ds.total_size_bytes) },
        { label: t('Created'), value: formatDateTime(ds.created_at) },
      ];
      const icon = ds.visibility === 'organization' ? <PiBuildingLight /> : <PiUserLight />;
      const collaborators = (ds.collaborators || []).map((shared: any) => ({
        UserID: shared.user_uuid,
        Email: shared.email,
        FullName: shared.name,
        ProfilePicture: shared.avatarUrl,
        Initials: shared.initials,
      }));
      return (
        <ListItem
          key={ds.uuid}
          title={ds.name}
          details={details}
          badges={badges}
          onClick={() => history.push(`/data-sources/${ds.uuid}`)}
          link={`/data-sources/${ds.uuid}`}
          icon={icon}
          collaborators={collaborators}
        />
      );
    });
  }, [allDataSources, history]);

  return (
    <div className="text-text-light dark:text-text-dark transition-colors duration-300 flex flex-col h-full">
      {!isCreatingDataSource && (
        <>
          <div data-tour-id="ds-list-header">
            <Header actions={actions} />
          </div>
          <div data-tour-id="ds-list-filter-bar">
          <FilterBarWithTabs
            filters={filters}
            sortOptions={viewMode === 'grid' ? sortOptions : []}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            onSortChange={viewMode === 'grid' ? handleSortChange : undefined}
            onSearchChange={handleSearchChange}
            sortValue={`${sortBy}_${sortOrder}`}
            onClearAllFilters={() => setSelectedFilters({})}
            viewMode={viewMode}
            setViewMode={setViewMode}
            actions={[
              ...dataSourceActions.map(action => ({
                label: action.label,
                onClick: () => action.onClick(selectedDataSources),
                icon: action.icon,
              })),
              ...(showTrash ? trashBulkActions : []),
            ]}
            selectionCount={showTrash ? trashSelectedCount : (selectedDataSources.length > 0 ? selectedDataSources.length : undefined)}
            recycleBinEnabled
            recycleBinActive={showTrash}
            recycleBinLabel={t('Data Sources')}
            onRecycleBinToggle={(active) => { if (!active) fetchInitialDataSources(); setShowTrash(active); }}
          />
          </div>
        </>
      )}

      {showTrash ? (
        <ResourceTrash
          entityType="data_source"
          entityLabel="data sources"
          emptyIcon={<PiDatabaseLight />}
          onItemRestored={() => fetchInitialDataSources()}
          onBulkActionsChange={setTrashBulkActions}
          onSelectedCountChange={setTrashSelectedCount}
        />
      ) : (
      <div className="flex-grow overflow-y-auto">
        {isCreatingDataSource ? (
          <div className="flex flex-col overflow-hidden relative h-screen">
              <CreateDataSourceForm onCancel={handleAddDataSourceClose} />
          </div>
        ) : (
          <div className="flex-grow flex flex-col">

            <div className="flex-grow overflow-y-auto">
              <div className="animate-fade-in">
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-auto max-h-[calc(100vh-120px)] custom-scrollbar-main px-4">
                    {gridData}
                    {isLoading && <SkeletonClientItem viewMode="grid" count={3} />}
                    {allDataSources.length === 0 && !isLoading && (
                      <div className="col-span-full flex flex-col justify-center items-center h-full gap-4 max-w-md mx-auto text-center my-28">
                        <div className="text-gray-400 dark:text-gray-500">
                          <PiDatabaseLight size={48} />
                        </div>
                        <h2 className="text-lg text-gray-900 dark:text-gray-100">
                          {searchQuery || totalActiveFilters > 0 ? t('No results found.') : t('No data sources are created.')}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {searchQuery || totalActiveFilters > 0
                            ? t("Try adjusting your search or filters to find what you're looking for.")
                            : t('Create external data sources to integrate information seamlessly into your legal workflows and analyses.')}
                        </p>
                        <Button
                          variant="primary"
                          size="small"
                          onClick={searchQuery || totalActiveFilters > 0 ? () => { setSearchQuery(''); setSelectedFilters({}); } : handleAddDataSourceClick}
                        >
                          {searchQuery || totalActiveFilters > 0 ? t('Clear Search & Filters') : t('Create New Data Source')}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="relative">
                    <div
                      data-tour-id="ds-list-table"
                      className="absolute top-0 left-0 right-0 pointer-events-none"
                      style={{ height: '120px' }}
                    />
                    {isLoading && allDataSources.length === 0 ? (
                      <SkeletonClientItem viewMode="table" count={5} />
                    ) : (
                      <Table
                        columns={columns}
                        data={tableData}
                        enableSelection
                        selectedIds={selectedDataSources}
                        onSelect={(id) => setSelectedDataSources(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                        onSelectAll={() => setSelectedDataSources(selectedDataSources.length === allDataSources.length ? [] : allDataSources.map(ds => ds.uuid))}
                        onRowClick={(row) => {
                          const status = row.processing_status;
                          if (status && status !== 'active') {
                            if (status === 'indexing') {
                              dispatch(showFeedbackModal({
                                showModal: true,
                                message: t('This data source is still being indexed. Please wait until indexing completes.'),
                                modalType: 'info',
                                duration: 3000,
                              }));
                            } else if (status === 'failed') {
                              dispatch(showFeedbackModal({
                                showModal: true,
                                message: t('This data source failed to index.'),
                                modalType: 'warning',
                                duration: 3000,
                              }));
                            }
                            return;
                          }
                          history.push(`/data-sources/${row.id}`);
                        }}
                        actions={tableActions}
                        hasMore={hasMore}
                        isLoadingMore={isLoading}
                        onLoadMore={loadMoreDataSources}
                        sortableColumns={sortableColumns}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={handleSortChange}
                        total_items={totalDataSources}
                        total_showing_items={allDataSources.length}
                        setIsShowing={true}
                        showingLabel={t('data sources')}
                        selectedCount={selectedDataSources.length}
                        resizableColumns={true}
                        defaultColumnWidths={{
                          'Name': 280,
                          'Status': 120,
                          'Documents': 110,
                          'Size': 100,
                          'Access': 180,
                          'Created At': 180,
                        }}
                        storageKeyPrefix="datasources"
                        emptyState={{
                          icon: <PiDatabaseLight />,
                          headline: searchQuery || totalActiveFilters > 0 ? t('No results found.') : t('No data sources are created.'),
                          subtext: searchQuery || totalActiveFilters > 0
                            ? t("Try adjusting your search or filters to find what you're looking for.")
                            : t('Create external data sources to integrate information seamlessly into your legal workflows and analyses.'),
                          button: {
                            label: searchQuery || totalActiveFilters > 0 ? t('Clear Search & Filters') : t('Create New Data Source'),
                            onClick: searchQuery || totalActiveFilters > 0 ? () => { setSearchQuery(''); setSelectedFilters({}); } : handleAddDataSourceClick,
                          },
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      )}
      <FeatureTour
        steps={dataSourcesListTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
};

export default DataSourcesListPage;
